import { useParams } from "react-router-dom";
import { useNavigator } from "../../../../framework/services/navigation";
import { useToast } from "../../../../framework/components/bwj-toast/context";
import * as formService from "../../form-services/publish-handle";
import { useEffect, useState } from "react";
import { BwjViewManager, BwjViewState } from "../../../../framework/components/bwj-view-manager";
import { ILowesStormDoorHandleEditingModel, LowesStormDoorHandleEditingModel } from "../../../../entities-generated/models/lowes-storm-door-handle/lowes-storm-door-handle-editing-model";
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from "../../../../framework/components/bwj-confirmation-dialog";
import { LowesStormDoorHandleApiClient } from "../../../../services/lowes-storm-door-handle";
import { MessageBarType, Text } from "@fluentui/react";
import { AppRoutes } from "../../../app-routes";
import { BwjImageBrowser, IBrowserImage } from "../../../../framework/components/bwj-image-browser";
import { remove } from "../../../../framework/utils/collection";
import { BwjPageHeader } from "../../../../framework/components/bwj-page-header";
import { IStormDoorHandleTypeOption } from "../../../../entities-generated/common/storm-door-handle-type-option";
import { BwjGrid, Row, Col } from "../../../../framework/components/bwj-layout";
import { Spacing } from "../../../../framework/components/bwj-layout/styles";
import { BwjPageContent } from "../../../../framework/components/bwj-page-content";
import { BwjSingleSelectField } from "../../../../framework/components/bwj-select-field/single";
import { BwjTextField } from "../../../../framework/components/bwj-text-field";
import { BwjEventArgs } from "../../../../framework/entities/bwj-event";

const subscriberId = 'Page.PublishLowesStormHandle';

export const PagePublishLowesStormHandle: React.FunctionComponent = () => {
  const { id: handleId } = useParams<{id: string}>();
  const nav = useNavigator();
  const { showToast } = useToast();
  const formModel = formService.useFormModel(subscriberId);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [handle, setHandle] = useState<ILowesStormDoorHandleEditingModel>(new LowesStormDoorHandleEditingModel());
  const [auxImageDisplayIndex, setAuxImageDisplayIndex] = useState(0);
  const [displayProps, setDisplayProps] = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);
  
  useEffect(() => {
    LowesStormDoorHandleApiClient.getImportedStormDoorHandle(handleId)
    .then(d => {
      setHandle(d);
      formService.initializeService(d);
      setViewState(BwjViewState.Ready);
    })
    .catch(() => setViewState(BwjViewState.Error));
  }, [handleId])

  const onPublishHandle = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return null; }
      
      setViewState(BwjViewState.Loading);
      const request = formService.toPublishRequest();
      return LowesStormDoorHandleApiClient.publishStormDoorHandle(request, handleId);
    })
    .then(resp => {
      if(!resp) { return; }
      showToast(MessageBarType.success, `Storm door handle '${formModel.description.value}' successfully published`);
      nav.goTo(AppRoutes.importedLowesStormHandles.url());
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    });
  }

  const onProceedWithRejection = () => {
    setDisplayProps(undefined);
    setViewState(BwjViewState.Loading);

    const request = formService.toPublishRejectionRequest();
    LowesStormDoorHandleApiClient.publishStormDoorHandle(request, handleId)
    .then(() => {
      showToast(MessageBarType.info, `Storm door handle '${formModel.description.value}' was rejected`);
      nav.goTo(AppRoutes.importedLowesStormHandles.url());
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    });
  }

  const onRejectStormDoorHandle = () => {
    const dp = {
      title: 'Reject Storm Door Handle',
      message: 'Are you sure you want to block this and future publishing of this product?',
      proceedPrompt: 'Yes, reject',
      cancelPrompt: 'Cancel',
      onProceedRequested: onProceedWithRejection,
      onCancelRequested: () => {}
    };
    setDisplayProps(new BwjEventArgs(dp));
  }

  const onViewOnVendorSite = () => {
    window.open(`https://lowes.com${handle.vendorUrl}`, '_blank');
  }

  const onAuxImageRemoved = (image: IBrowserImage) => {
    const imageUrls = remove(formModel.imageUrls.value, i => i === image.src);
    formService.imageUrls.onUpdate(imageUrls);
  }

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Loading content...'
      }}
      errorState={{
        message: 'An unexpected error occurred'
      }}
    >
      <BwjConfirmationDialog displayArgs={displayProps} />
      <BwjPageHeader
        title={handle.description}
        titleIconName={AppRoutes.publishLowesStormHandle.icon}
        actionButtons={[{
          id: 'btnPublish',
          name: 'Publish handle',
          iconName: 'PublishContent',
          onClick: onPublishHandle,
        }, {
          id: 'btnReject',
          name: 'Reject handle',
          iconName: 'Blocked',
          onClick: onRejectStormDoorHandle,
        }, {
          id: 'btnViewAtVendor',
          name: 'View on vendor site',
          iconName: 'Globe',
          onClick: onViewOnVendorSite,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <BwjTextField
                id={formService.description.id}
                required={true}
                label={formService.description.description}
                onUpdate={formService.description.onUpdate}
                input={formModel.description}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <div id={formService.imageUrls.id}>
                <Text variant='medium' style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>{formService.imageUrls.description}</Text>
                <div>
                  <BwjImageBrowser
                    id={`${formService.imageUrls.id}-browser`}
                    images={formModel.imageUrls.value.map(x => { return { src: x }; })}
                    displayIndex={auxImageDisplayIndex}
                    loadImages={true}
                    imageWidth={400}
                    contextButton={{
                      icon: 'Delete',
                      onClick: onAuxImageRemoved
                    }}
                    onDisplayIndexChanged={index => setAuxImageDisplayIndex(index)}
                  />
                </div>
              </div>
            </Col>
            <Col small={12} large={6}>
              <BwjSingleSelectField<IStormDoorHandleTypeOption>
                id={formService.handleType.id}
                required={true}
                label={formService.handleType.description}
                onUpdate={formService.handleType.onUpdate}
                input={formModel.handleType}
                options={formService.handleType.options}
                optionDisplayKey={'description'}
                optionValueKey={'id'}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <BwjTextField
                id={formService.sellingPoints.id}
                required={false}
                label={formService.sellingPoints.description}
                onUpdate={formService.sellingPoints.onUpdate}
                input={formModel.sellingPoints}
                multiline={true}
              />
            </Col>
          </Row>
        </BwjGrid>
      </BwjPageContent>
    </BwjViewManager>
  );
};